import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { i18nTranslationHelper } from '@/i18n'
import {
  makeSkillWebManagerUsersRedirectUrl,
  makeSkillWebMyProfileRedirectUrl,
  makeSkillWebMyProfileRefundsRedirectUrl
} from '@/main/factories/http'
import {
  computeContentValueIdHelper,
  findRouteByCategoryGroupHelper,
  findRouteByCategoryHelper,
  findRouteByPathnameHelper,
  getCategoriesPathsHelper,
  getCategoryGroupPathsHelper,
  getCategoryGroupRoutePathHelper,
  getCategoryRoutePathHelper,
  isInStaticRouteHelper
} from './helpers/path-utils'

const t = i18nTranslationHelper.getTPage

// ============================================
// TYPES
// ============================================

export type RouteConfig = {
  value: string
  name: string
  title?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  computeValue?: (args: any) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  computeTitle?: (args: any) => string
}

export type RoutesProps = {
  [key: string]: RouteConfig
}

// ============================================
// AUTHENTICATED (PRIVATE) ROUTES
// ============================================

const PRIVATE_ROUTES = {
  INDEX: {
    value: '/',
    name: 'home',
    title: t('home.pageTitle')
  },
  HOME: {
    value: '/',
    name: 'home',
    title: t('home.pageTitle')
  },
  SEARCH: {
    value: '/search',
    name: 'search',
    title: 'Busca'
  },
  SKILLS: {
    value: '/skills/:id',
    name: 'skill',
    title: 'Habilidades',
    computeValue: (args: { id: string | number }) => `/skills/${args.id}`
  },
  INSTITUTIONS: {
    value: '/partners',
    name: 'partners',
    title: t('partners.pageTitle')
  },
  INSTITUTION: {
    value: '/partners/:id',
    name: 'partner',
    computeValue: (args: { id: string | number }) => `/partners/${args.id}`
  },
  CONTENT: {
    value: '/content/:id',
    name: 'content',
    computeValue: ({ content }: { content: { id: number; productId: number } }) =>
      `/content/${computeContentValueIdHelper(content)}`
  },
  ...getCategoriesPathsHelper(),
  ...getCategoryGroupPathsHelper(),

  // Content Actions
  CONTENT_ENROLLMENT: {
    value: '/content/:id/enrollment',
    name: 'content_enrollment',
    computeValue: (id: string | number) => `/content/${id}/enrollment`
  },
  CONTENT_UNSUBSCRIBE: {
    value: '/content/enrollment/:id/unsubscribe',
    name: 'content_unsubscribe',
    computeValue: (args: { id: string | number }) => `/content/enrollment/${args.id}/unsubscribe`
  },
  CONTENT_COMPLETE: {
    value: '/content/enrollment/:id/complete',
    name: 'content_complete',
    computeValue: (args: { id: string | number }) => `/content/enrollment/${args.id}/complete`
  },

  // Onboarding
  ONBOARDING: {
    value: '/onboarding',
    name: 'onboarding'
  },
  ONBOARDING_CHECK: {
    value: '/onboarding/check',
    name: 'onboarding_check'
  },
  ONBOARDING_1: {
    value: '/onboarding/1',
    name: 'onboarding_1'
  },
  ONBOARDING_PERSONAL_DATA: {
    value: '/onboarding/personal-data',
    name: 'onboarding_personal_data'
  },
  ONBOARDING_PROFESSIONAL_TRAJECTORY: {
    value: '/onboarding/professional-trajectory',
    name: 'professional_trajectory'
  },
  ONBOARDING_CAREER_MOMENT: {
    value: '/onboarding/career-moment',
    name: 'onboarding_career_moment'
  },
  ONBOARDING_LEARNING_OBJECTIVES: {
    value: '/onboarding/learning-objectives',
    name: 'onboarding_learning_objectives'
  },
  ONBOARDING_CONTENT_TYPES: {
    value: '/onboarding/content-types',
    name: 'onboarding_content_types'
  },
  ONBOARDING_CONTENT_FORMATS: {
    value: '/onboarding/content-formats',
    name: 'onboarding_content_formats'
  },
  ONBOARDING_3: {
    value: '/onboarding/3',
    name: 'onboarding_3'
  },
  ONBOARDING_4: {
    value: '/onboarding/4',
    name: 'onboarding_4'
  },
  ONBOARDING_SECURITY: {
    value: '/onboarding/security',
    name: 'onboarding_security'
  },
  ONBOARDING_COMPLETED: {
    value: '/onboarding/completed',
    name: 'onboarding_completed'
  },

  // User Profile & Settings
  USER_PROFILE: {
    value: '/user/profile',
    name: 'user_profile',
    title: t('userProfile.pageTitle')
  },
  USER_INSTEREST_AREAS_SKILLS: {
    value: '/user/interest-areas-skills',
    name: 'user_interest_areas_skills',
    title: t('userInterestAreasSkills.pageTitle')
  },
  USER_SETTINGS: {
    value: '/user/settings',
    name: 'user_settings',
    title: t('userSettings.pageTitle')
  },
  USER_ENROLLMENTS: {
    value: makeSkillWebMyProfileRedirectUrl(),
    name: 'monolith_user_profile',
    title: t('userEnrollments.pageTitle')
  },
  USER_ENROLLMENTS_REFUNDS: {
    value: makeSkillWebMyProfileRefundsRedirectUrl(),
    name: 'monolith_user_profile_refunds',
    title: t('userEnrollments.pageTitle')
  },
  USER_SPACE: {
    value: '/user/space',
    name: 'user_space',
    title: t('userSpace.pageTitle')
  },
  USER_ENROLLMENTS_V2: {
    value: '/user/enrollments',
    name: 'user_enrollments',
    title: t('userEnrollments.pageTitle')
  },
  USER_ENROLLMENT_DETAIL: {
    value: '/user/enrollments/:enrollmentId',
    name: 'user_enrollment_detail',
    title: t('userEnrollmentDetail.pageTitle'),
    computeValue: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `/user/enrollments/${enrollmentId}`,
    computeTitle: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `${t('userEnrollmentDetail.pageTitle')}${enrollmentId}`
  },
  USER_ENROLLMENT_ENGAGEMENT: {
    value: '/user/enrollments/:enrollmentId/engagement',
    name: 'user_enrollment_engagement',
    title: t('userEnrollmentEngagement.pageTitle'),
    computeValue: ({ enrollmentId }: { enrollmentId: string | number }) =>
      `/user/enrollments/${enrollmentId}/engagement`
  },
  FAVORITES: {
    value: '/user/favorites',
    name: 'favorites',
    title: t('favorites.pageTitle')
  },

  // Company Manager
  COMPANY_MANAGER_DASHBOARD: {
    value: '/admin/dashboard',
    name: 'company_manager_dashboard',
    title: t('companyManager.dashboard.pageTitle')
  },
  COMPANY_MANAGER_CREATE_USER_PROFILE: {
    value: '/admin/user',
    name: 'company_manager_create_user_profile',
    title: t('companyManager.createUserProfile.pageTitle')
  },
  COMPANY_MANAGER_EDIT_USER_PROFILE: {
    value: '/admin/user/:accountId/profile',
    name: 'company_manager_edit_user_profile',
    title: t('companyManager.editUserProfile.pageTitle'),
    computeValue: (args: { accountId: string | number }) => `/admin/user/${args.accountId}/profile`
  },
  COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE: {
    value: '/admin/dependent_user/:accountId/profile',
    name: 'company_manager_edit_dependent_user_profile',
    title: t('companyManager.editDependentUserProfile.pageTitle'),
    computeValue: (args: { accountId: string | number }) =>
      `/admin/dependent_user/${args.accountId}/profile`
  },
  COMPANY_MANAGER_DEPENDENT_LIST: {
    value: '/admin/dependent-user',
    name: 'company_manager_dependent_list',
    title: t('companyManager.dependentList.pageTitle')
  },
  COMPANY_MANAGER_HOLDER_SEARCH: {
    value: '/admin/holder-user/search',
    name: 'company_manager_holder_search',
    title: t('companyManager.holderSearch.pageTitle')
  },
  COMPANY_MANAGER_DEPENDENT_REGISTRATION: {
    value: '/admin/holder-user/:accountId/dependent',
    name: 'company_manager_dependent_registration',
    title: t('companyManager.dependentRegistration.pageTitle'),
    computeValue: (args: { accountId: string | number }) =>
      `/admin/holder-user/${args.accountId}/dependent`
  },
  COMPANY_MANAGER_REQUESTS_CONFIGURATION: {
    value: '/admin/requests/configuration',
    name: 'company_manager_requests_configuration',
    title: t('companyManager.requestsConfiguration.pageTitle')
  },
  COMPANY_MANAGER_DEPENDENT_ENROLLMENT: {
    value: '/admin/dependent-enrollment',
    name: 'company_manager_dependent_enrollment',
    title: t('companyManager.dependentEnrollment.pageTitle')
  },
  COMPANY_MANAGER_UPGRADE_ENROLLMENT: {
    value: '/admin/upgrade-enrollment',
    name: 'company_manager_upgrade_enrollment',
    title: t('companyManager.upgradeEnrollment.pageTitle')
  },
  COMPANY_MANAGER_AUTHORIZATIONS: {
    value: '/admin/authorizations',
    name: 'company_manager_authorizations',
    title: t('companyManager.authorizations.pageTitle')
  },
  COMPANY_MANAGER_AUTHORIZATION_DETAIL: {
    value: '/admin/authorizations/:authorizationId',
    name: 'company_manager_authorization_detail',
    title: t('companyManager.authorizationDetail.pageTitle'),
    computeValue: ({ authorizationId }: { authorizationId: string | number }) =>
      `/admin/authorizations/${authorizationId}`,
    computeTitle: ({ authorizationId }: { authorizationId: string | number }) =>
      `${t('companyManager.authorizationDetail.pageTitle')} #${authorizationId}`
  },
  COMPANY_MANAGER_COLLABORATORS: {
    value: '/admin/collaborators',
    name: 'company_manager_collaborators',
    title: t('companyManager.collaborators.pageTitle')
  },
  COMPANY_MANAGER_GROUPS: {
    value: '/admin/groups',
    name: 'company_manager_groups',
    title: t('companyManager.groups.pageTitle')
  },

  // Internal Company Manager (Backoffice)
  INTERNAL_COMPANY_MANAGER_USER_BATCH_DISABLE: {
    value: '/backoffice/user/batch-disable',
    name: 'internal_company_manager_user_batch_disable',
    title: t('internalCompanyManager.userBatchDisable.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_INVOICE_DETAIL: {
    value: '/backoffice/invoice/:id',
    name: 'internal_company_manager_invoice_detail',
    title: t('internalCompanyManager.invoiceDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/invoice/${args.id}`
  },
  INTERNAL_COMPANY_MANAGER_INVOICE: {
    value: '/backoffice/invoice',
    name: 'internal_company_manager_invoice',
    title: t('internalCompanyManager.invoice.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANIES: {
    value: '/backoffice/companies',
    name: 'internal_company_manager_companies',
    title: t('internalCompanyManager.companies.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANY_CREATE: {
    value: '/backoffice/companies/create',
    name: 'internal_company_manager_company_create',
    title: t('internalCompanyManager.companyCreate.pageTitle')
  },
  INTERNAL_COMPANY_MANAGER_COMPANY_DETAIL: {
    value: '/backoffice/company/:id',
    name: 'internal_company_manager_company_detail',
    title: t('internalCompanyManager.companyDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/company/${args.id}`
  },
  INTERNAL_COMPANY_MANAGER_PARTNER_DETAIL: {
    value: '/backoffice/partner/:id',
    name: 'internal_company_manager_partner_detail',
    title: t('internalCompanyManager.partnerDetail.pageTitle'),
    computeValue: (args: { id: string | number }) => `/backoffice/partner/${args.id}`
  },

  // System Manager
  SYSTEM_MANAGER_CONFIGURATIONS: {
    value: '/backoffice/system/configurations',
    name: 'system_manager_configurations',
    title: t('systemManager.configurations.pageTitle')
  },

  // CB Unico & Profile Assessment
  CBU_START: {
    value: '/cbu/start',
    name: 'cbu_start'
  },
  PROFILE_ASSESSMENT_CALLBACK: {
    value: '/assessment/callback',
    name: 'profile_assessment_callback'
  },
  PROFILE_ASSESSMENT_IN_PROGRESS: {
    value: '/assessment/progress',
    name: 'profile_assessment_in_progress'
  },
  PROFILE_ASSESSMENT_GET_STARTED: {
    value: '/assessment/get-started',
    name: 'profile_assessment_get_started'
  },

  // Refunds
  REFUND: {
    value: '/refund',
    name: 'refund_program_list',
    title: t('refund.title')
  },
  REFUND_REQUEST: {
    value: '/refund/program/:id/request',
    name: 'refund_request',
    computeValue: (args: { id: string | number }) => `/refund/program/${args.id}/request`
  },

  // Upgrade Requests
  UPGRADE_REQUEST: {
    value: '/upgrade-request',
    name: 'upgrade_request'
  },

  // Dependent Requests
  DEPENDENT_REQUEST: {
    value: '/dependent-request',
    name: 'dependent_request'
  },
  DEPENDENT_REQUEST_CATALOG: {
    value: '/dependent-request/catalog',
    name: 'dependent_request_catalog',
    title: t('dependentRequest.catalog.pageTitle')
  },
  DEPENDENT_REQUEST_CONTENT: {
    value: '/dependent-request/catalog/:id',
    name: 'dependent_request_content',
    computeValue: ({ content }: { content: { id: number; productId: number } }) =>
      `/dependent-request/catalog/${computeContentValueIdHelper(content)}`
  },

  // Contact Verification
  CONTACT_VERIFICATION: {
    value: '/contact-verification',
    name: 'contact_verification',
    title: t('contactVerification.title')
  }
} as const

// ============================================
// UNAUTHENTICATED (PUBLIC) ROUTES
// ============================================

const PUBLIC_ROUTES = {
  LOGIN: {
    value: '/login',
    name: 'login',
    title: 'Login'
  },
  LOGOUT: {
    value: '/logout',
    name: 'logout',
    title: 'Logout'
  },
  FORGOT_PASSWORD: {
    value: '/forgot-password',
    name: 'forgot_password',
    title: 'Recuperar Senha'
  },
  RESET_PASSWORD: {
    value: '/reset-password',
    name: 'reset_password',
    title: 'Redefinir Senha'
  },
  RESEND_ACCESS: {
    value: '/resend-access',
    name: 'resend_access',
    title: 'Reenviar Acesso'
  },
  CB_UNICO_SUCCESS: {
    value: '/cbu/success',
    name: 'cbu_success'
  },
  FIRST_ACCESS: {
    value: '/first-access',
    name: 'first_access'
  },
  FIRST_ACCESS_CPF: {
    value: '/first-access/cpf',
    name: 'first_access_cpf'
  },
  SKILL_EXPLORER_CATALOG: {
    value: '/skill-explorer-catalog',
    name: 'skill_explorer_catalog'
  },
  LOGIN_FIRST_ACCESS: {
    value: '/login-first-access',
    name: 'login_first_access'
  }
} as const

// ============================================
// EXTERNAL ROUTES
// ============================================

const EXTERNAL_ROUTES = {
  MONOLITH_MANAGE_CUSTOMER: {
    name: 'monolith_manage_customers',
    value: makeSkillWebManagerUsersRedirectUrl(),
    title: t('externalRoutes.monolithManageCustomer.pageTitle')
  }
} as const

// ============================================
// WRAPPER FUNCTIONS (use helpers with app-specific routes)
// ============================================

export const findRouteByPathname = (pathname: string): RouteConfig | null => {
  return findRouteByPathnameHelper(pathname, PRIVATE_ROUTES)
}

export const findRouteByCategory = (category: Category.Enum) => {
  return findRouteByCategoryHelper(category, PRIVATE_ROUTES)
}

export const getCategoryRoutePath = (category: Category.Enum) => {
  return getCategoryRoutePathHelper(category, PRIVATE_ROUTES)
}

export const findRouteByCategoryGroup = (group: EnrollmentCategory.Enum) => {
  return findRouteByCategoryGroupHelper(group, PRIVATE_ROUTES)
}

export const getCategoryGroupRoutePath = (group: EnrollmentCategory.Enum) => {
  return getCategoryGroupRoutePathHelper(group, PRIVATE_ROUTES)
}

export const isInStaticRoute = () => {
  return isInStaticRouteHelper(PUBLIC_ROUTES)
}

// ============================================
// BACKWARD COMPATIBILITY
// Export PathConstants namespace for existing code
// ============================================

export namespace PathConstants {
  export type RouteProps = RouteConfig
  export type RoutesProps = {
    [key: string]: RouteConfig
  }
}

const ALL_ROUTES = {
  ...PRIVATE_ROUTES,
  ...PUBLIC_ROUTES,
  ...EXTERNAL_ROUTES
}

export const PathConstants = {
  Routes: ALL_ROUTES,
  findRouteByPathname,
  findRouteByCategory,
  getCategoryRoutePath,
  findRouteByCategoryGroup,
  getCategoryGroupRoutePath,
  isInStaticRoute
}

export default PathConstants
